import { z } from 'zod';

/**
 * `pending-test-ownership-oncall` v0 — wire types for the
 * operationally-meaningful "owner" axis on flows.
 *
 * Locked decisions (2026-05-28 design doc):
 *   - Single `owner_user_id` per flow in v0 (team-based ownership
 *     is v1; on-call rotation is v1).
 *   - NULL owner = "Unowned" — surfaced in the dashboard via the
 *     `Unowned (N flows)` pill (GET /api/orgs/ownership/unowned-count).
 *   - Owner assignment is a PATCH on the flow surfacing
 *     `ownerUserId: string | null`.
 *   - The v0 ship deliberately ships the data + API layer ONLY;
 *     auto-paging on 2-consecutive-failures and stale-flow
 *     detection workers land as separate ships once the column
 *     this contract pins is in place across the install base.
 */

export const FlowOwnerSchema = z.object({
  flowId: z.string(),
  ownerUserId: z.string().nullable(),
  /**
   * Optional convenience fields populated by the GET path so the
   * SPA's owner-pill can render without a second `users` round-trip.
   * Both are `null` when the flow has no owner OR the owner has
   * left the org (FK ON DELETE SET NULL).
   */
  ownerName: z.string().nullable().optional(),
  ownerEmail: z.string().nullable().optional(),
});
export type FlowOwner = z.infer<typeof FlowOwnerSchema>;

export const AssignFlowOwnerRequestSchema = z.object({
  /**
   * `null` un-assigns the owner; otherwise must be a valid user id
   * that is a member of the same org as the flow.
   */
  ownerUserId: z.string().nullable(),
});
export type AssignFlowOwnerRequest = z.infer<typeof AssignFlowOwnerRequestSchema>;

export const UnownedCountResponseSchema = z.object({
  /** Number of flows in this org with `owner_user_id IS NULL`. */
  unownedCount: z.number().int().nonnegative(),
  /** Total flows in the org (for the "N of M" pill display). */
  totalCount: z.number().int().nonnegative(),
});
export type UnownedCountResponse = z.infer<typeof UnownedCountResponseSchema>;
